import { FullWidthBackgroundContainer } from '@dreamstack/feature-components/src/layout/FullWidthBackgroundContainer'
import { GridWithGaps } from '@dreamstack/feature-components/src/layout/MainGrid'
import { assertPopulatedDeep } from '@dreamstack/payload/src/lib/assertPopulated'
import { BlockOneColumn } from '@dreamstack/payload/src/payload-types'
import { cn } from '@dreamstack/util'
import Image from 'next/image'
import type { FunctionComponent } from 'react'
import React from 'react'
import TailwindConfig from '../../../../tailwind.config'
import { ChildrenBlockSpacingContainer } from '../contentful/ChildrenBlockSpacingContainer'
import { ContentfulBlocks } from '../contentful/ContentfulBlocks'

// FALLBACK IF 100vw BREAKS LAYOUT
// const FullWidthBackgroundContainer = styled.div(() => [
//   tw`bg-accentroGray-full`,
//   ` width: calc(100vw - 24px);
//   z-index: -1;
//     position: absolute;
//     right: 50%;
//     left: 50%;
//     margin-left: -50vw;
//     margin-right: -50vw;
//     overflow: hidden;
//     height: calc(100% + 96px);
//     top: -48px;
//     @media (pointer: coarse){
//       width: 100vw;
//     }
//     `,
// ])

type GridWithOptionalImageProps = React.PropsWithChildren<{ bgSrc?: string }>
const GridWithOptionalImage: FunctionComponent<GridWithOptionalImageProps> = ({
  bgSrc,
  children,
}) => (
  <GridWithGaps
    {...(bgSrc ? { 'data-show-bg': true } : {})}
    className={cn('relative', !!bgSrc && 'py-px-24 md:py-px-48 lg:py-px-64')}
  >
    {/* Use a media-query style tag to conditionally apply background image only on >=880px to match previous behavior */}
    {bgSrc && (
      <style>{`@media (min-width:880px){[data-show-bg]{background-image:url(${bgSrc});background-position:center;background-size:cover;}}`}</style>
    )}
    <div className={cn('contents')}>{children}</div>
  </GridWithGaps>
)

type TextContainerProps = React.PropsWithChildren<{
  smallVersion: boolean
  addBackgroundPaddings: boolean
  padding?: string | null
}>
const TextContainer: FunctionComponent<TextContainerProps> = ({
  smallVersion,
  addBackgroundPaddings,
  padding,
  children,
}) => (
  <div
    className={cn(
      !smallVersion && 'col-span-full',
      smallVersion &&
        'bg-accentroGray-full col-span-full md:p-px-48 md:col-start-7 md:col-span-6 lg:col-start-8 lg:col-span-4',
      addBackgroundPaddings && 'py-px-24 ml:py-px-48',
      padding === 'none' && 'pb-0'
    )}
  >
    {children}
  </div>
)

type PossibleColors = {
  AccentroAquaVeryLight: string
  Black: string
}

const possibleColors: PossibleColors = {
  AccentroAquaVeryLight:
    TailwindConfig.theme.extend.colors.accentroAquaVeryLight.full,
  Black: TailwindConfig.theme.extend.colors.accentroGray.full,
}

export const OneColumnLayout: FunctionComponent<
  React.PropsWithChildren<{
    block: BlockOneColumn
  }>
> = ({ block }) => {
  const backgroundColor =
    block?.backgroundColor && block?.backgroundColor in possibleColors
      ? possibleColors[block.backgroundColor as keyof PossibleColors]
      : null
  const enableBlackBackground =
    !!block?.enableBlackBackground || block?.backgroundColor === 'Black'
  assertPopulatedDeep(block)
  const backgroundImage = block?.backgroundImage
  const imageSrc = backgroundImage?.url ?? undefined

  const showBackgroundComponent = enableBlackBackground || !!backgroundColor

  return (
    <>
      <GridWithOptionalImage bgSrc={imageSrc}>
        {showBackgroundComponent && (
          <FullWidthBackgroundContainer backgroundColor={backgroundColor} />
        )}
        <TextContainer
          smallVersion={!!block.smallMainColumnOverImage}
          addBackgroundPaddings={showBackgroundComponent}
          padding={block?.padding}
        >
          <ChildrenBlockSpacingContainer
            withExtraVerticaLPadding={
              !!block?.withExtraPadding || block?.padding === 'extra'
            }
            withExtraSideLPadding={!!imageSrc}
          >
            {!!imageSrc && (
              <div className={cn('relative w-full h-px-274 md:hidden')}>
                <Image
                  className={cn('margin[-48px] md:margin[0] object-cover')}
                  src={imageSrc}
                  fill
                  alt={backgroundImage?.alt || ''}
                />
              </div>
            )}
            <ContentfulBlocks
              blocks={block?.mainColumn ?? []}
              // blocks={filter((block as any).childBlockData)}
              customProps={{
                blackBackground:
                  enableBlackBackground || !!block.smallMainColumnOverImage,
              }}
            />
          </ChildrenBlockSpacingContainer>
        </TextContainer>
      </GridWithOptionalImage>
    </>
  )
}
